import CourseEditorExpandCard from './CourseEditorExpandCard'
import { Button, Icon } from '@bm-js/atlascloud-ui'
import { v4 as uuid } from 'uuid'
import Input from './Input'
import { KeyboardEvent, useState } from 'react'
import Select from './Select'
import {
  ChangeEvent,
  CourseBM,
  CourseTestQuestion,
  jsonCopy,
  LucideIconName,
} from '@bm-js/atlascloud-shared'

const initialQuestion: CourseTestQuestion = {
  type: 'multiplechoice',
  question: '',
  options: [],
  correctAnswer: '',
  uuid: '',
}

type Props = {
  course: CourseBM
  setCourse: (course: CourseBM) => void
}

const KnowledgeTestEdit = ({ course, setCourse }: Props) => {
  const [optionFields, setOptionFields] = useState<string[]>([])

  const setTest = (updated: CourseTestQuestion[]) => {
    setCourse({
      ...course,
      testQuestions: updated,
    })
  }

  const addQuestion = () => {
    const newQuestion = jsonCopy(initialQuestion)
    newQuestion.uuid = uuid()
    const updated = [...course.testQuestions]
    updated.push(newQuestion)
    setTest(updated)
  }

  const deleteQuestion = (i: number) => {
    const updated = [...course.testQuestions]
    updated.splice(i, 1)
    setTest(updated)
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleChange = (e: ChangeEvent<any>, i: number) => {
    const name = e.target.name as keyof CourseTestQuestion
    const updated = [...course.testQuestions]
    updated[i][name] = e.target.value
    setTest(updated)
  }

  const handleAddOption = (e: KeyboardEvent, i: number) => {
    if (e.key === 'Enter') {
      const updated = [...course.testQuestions]
      updated[i].options.push(optionFields[i])
      setTest(updated)
      const temp = [...optionFields]
      temp[i] = ''
      setOptionFields(temp)
    }
  }

  const removeOption = (index: number, optionIndex: number) => {
    const updated = [...course.testQuestions]
    updated[index].options.splice(optionIndex, 1)
    updated[index].correctAnswer = ''
    setTest(updated)
  }

  return (
    <div>
      <h2 className='mt-6 mb-2'>Kunskapsstest</h2>
      <div className='mb-2 h-px bg-slate-400' />
      {course.testQuestions.map((question, i) => (
        <CourseEditorExpandCard
          key={question.uuid}
          header={`Fråga ${i + 1}`}
          extraActions={
            <Icon
              name={LucideIconName.Trash2}
              className='cursor-pointer size-5'
              onClick={() => deleteQuestion(i)}
            />
          }
        >
          <Input
            type='text'
            label='Frågeställning'
            desc='Frågan som visas för användaren'
            value={question.question}
            name='question'
            onChange={(e) => handleChange(e, i)}
          />
          <Input
            type='text'
            label='Svarsalternativ'
            value={optionFields[i] || ''}
            placeholder='Skriv in svarsalternativet och tryck på Enter'
            onChange={(e) => {
              const temp = [...optionFields]
              temp[i] = e.target.value
              setOptionFields(temp)
            }}
            onKeyDown={(e) => handleAddOption(e, i)}
          />
          {!!question.options.length && (
            <div className='ml-4 mb-6'>
              {question.options.map((option, oi) => (
                <div
                  key={`${question.uuid}_${option}_${oi}`}
                  className='flex gap-4'
                >
                  <p>- {option}</p>
                  <Icon
                    name={LucideIconName.Trash2}
                    onClick={() => removeOption(i, oi)}
                    className='cursor-pointer size-5'
                  />
                </div>
              ))}
            </div>
          )}
          <Select
            label='Rätt svar'
            value={question.correctAnswer}
            name='correctAnswer'
            onChange={(e) => handleChange(e, i)}
          >
            <option value='' disabled>
              Välj vilket svar som är rätt
            </option>
            {question.options.map((option, oi) => (
              <option
                key={`${question.uuid}_${option}_${oi}_answer`}
                value={option}
              >
                {option}
              </option>
            ))}
          </Select>
        </CourseEditorExpandCard>
      ))}
      <div className=' flex justify-end'>
        <Button onClick={addQuestion}>Lägg till fråga</Button>
      </div>
    </div>
  )
}

export default KnowledgeTestEdit
